/**
 * In order to slide to items/groups from outside, you can trigger events
 * on the ".slider" wrap:
 * 	- slide-to-item (passing { itemIndex: number })
 * 	- slide-to-prev-item
 * 	- slide-to-next-item
 * 	- slide-to-group (passing { groupIndex: number })
 * 	- slide-to-prev-group
 * 	- slide-to-next-group
 *
 * You can listen for the ".slider" wrap's "change" and "reset" events, which will
 * yield the following data:
 *  - itemIndex
 * 	- numPerGroup
 * 
 */


import { ceil, each, floor, max, range, throttle } from "lodash";
import {
    getItemsPerGroupDataFromWrapAttr,
    getWaitValueFromWrapAttr,
    getItemsPerGroup,
    ItemsPerGroupSetting,
    findHighestItemsPerGroupValue,
    highlightGroupControl,
} from "./slider-helpers";
import { findMaxHeight } from "./utils/layout";
import { zeroPadNumber } from "./utils/misc";

declare const jQuery: any;
const $ = jQuery;

const transitionend: string = `transitionend webkitTransitionEnd oTransitionEnd`;

const config = Object.freeze({
	dfItemsPerGroup: [
		{ minWindowWidth: 0, nItems: 1 },
	]
});

export function initInfiniteSliders(): void
{
	const $window = $(window);
	$(".infinite-slider").each(function () {
		initSlider($(this), $window);
	});
}

function initSlider($wrap, $window): void
{
    const itemsPerGroupSettings = getItemsPerGroupDataFromWrapAttr($wrap, config.dfItemsPerGroup);
    makeEnoughOriginalItems($wrap, itemsPerGroupSettings);
    const $outer = $wrap.find(".slider-items-display").first();
    const outer: HTMLDivElement = $outer[0];
    const $origScroller = $outer.find(".slider-items-slider").first();
    const $origItems = $origScroller.children().detach();
    const numOrigItems: number = $origItems.length;
    const origCount: number = parseInt($wrap.attr("data-count"), 10);
    const autoSlideWait: number|undefined = getWaitValueFromWrapAttr($wrap);
    const disableOnFew: boolean = $wrap.is("[data-disable-on-few]");
    const chevronsByItem: boolean = $wrap.is("[data-chevrons-by-item]");
    
    let numPerGroup: number;
    let itemIndex: number = 0;
    let itemHeightCache: number;
    let disabled: boolean = false;
    let $currScroller = $origScroller;
    let autoslideTimer: any;
    let autoSlideCancelled: boolean = !autoSlideWait;

    function init(): void
    {
        numPerGroup = getItemsPerGroup($window, itemsPerGroupSettings);
        const tooFew: boolean = disableOnFew && fewItems();
        const $items = tooFew ? getItemSlice(0, origCount).slice(0, origCount) : getItemSlice(itemIndex, numPerGroup);
        $currScroller.empty().append($items);
        const itemWidth: number = getTargetItemWidth();
        const itemHeight: number = itemHeightCache = getTargetItemHeight();
        $items.each(function (index: number) {
            $(this).css({ left: index * itemWidth, width: itemWidth, height: itemHeight });
        });
        $currScroller.css({ height: itemHeight });
        $outer.css({ height: itemHeight });
        disabled = false;
        $wrap.trigger("reset", { itemIndex, numPerGroup });
        highlightGroupControl($wrap, getGroupIndex());
        if (tooFew)
        {
            $wrap.addClass("hide-controls");
        }
        else
        {
            $wrap.removeClass("hide-controls");
        }
    }

    // Whether the number of items to display (numPerGroup) is greater than the number of original items (origCount)
    function fewItems(): boolean
    {
        return numPerGroup >= origCount;
    }

    function movePrevBy(numItemMove: number): void
    {
        if (disabled)
        {
            return;
        }
        disabled = true;
        clearTimeout(autoslideTimer);
        let x = itemIndex - numItemMove;
        while (x < 0)
        {
            x += numOrigItems;
        }
        const sliceStartIndex: number = x % numOrigItems;
        itemIndex = sliceStartIndex;
        const itemWidth: number = getTargetItemWidth();
        const $newMovingItems = getItemSlice(sliceStartIndex, numItemMove).slice(0, numItemMove);
        const numNewItems: number = $newMovingItems.length;
        $newMovingItems.each(function (index: number) {
            $(this).css({
                left: (-numNewItems + index) * itemWidth,
                width: itemWidth,
                height: itemHeightCache
            }).attr("data-index", index).appendTo($currScroller);
        });
        const $newScroller = $currScroller.clone().empty().css({
            zIndex: 0,
            transform: ""
        }).appendTo($outer);
        getItemSlice(itemIndex, numPerGroup).appendTo($newScroller).each(function (index: number) {
            $(this).css({
                left: index * itemWidth,
                width: itemWidth,
                height: itemHeightCache
            }).attr("data-index", index);
        });
        const slideDistance: number = itemWidth * numItemMove;
        $currScroller.off(transitionend).one(transitionend, () => {
            $currScroller.remove();
            $newScroller.css({ zIndex: "" });
            $currScroller = $newScroller;
            disabled = false;
            triggerChangeEvent();
            if (autoSlideWait && !autoSlideCancelled)
            {
                nextAutoSlide();
            }
        }).css({ transform: `translateX(${slideDistance}px)` });
        highlightGroupControl($wrap, getGroupIndex());
    }

    function moveNextBy(numItemMove: number): void
    {
        if (disabled)
        {
            return;
        }
        disabled = true;
        clearTimeout(autoslideTimer);
        triggerPreChangeEvent();
        const sliceStartIndex: number = (itemIndex + numPerGroup) % numOrigItems;
        itemIndex = (itemIndex + numItemMove) % numOrigItems;
        const itemWidth: number = getTargetItemWidth();
        getItemSlice(sliceStartIndex, numItemMove).each(function (index: number) {
            $(this).css({
                left: (numPerGroup + index) * itemWidth,
                width: itemWidth,
                height: itemHeightCache
            }).attr("data-index", index).appendTo($currScroller);
        });
        const $newScroller = $currScroller.clone().empty().css({
            zIndex: 0,
            transform: ""
        }).appendTo($outer);
        getItemSlice(itemIndex, numPerGroup).appendTo($newScroller).each(function (index: number) {
            $(this).css({
                left: index * itemWidth,
                width: itemWidth,
                height: itemHeightCache
            }).attr("data-index", index);
        });
        const slideDistance: number = itemWidth * numItemMove;
        $currScroller.off(transitionend).one(transitionend, () => {
            $currScroller.remove();
            $newScroller.css({ zIndex: "" });
            $currScroller = $newScroller;
            disabled = false;
            triggerChangeEvent();
            if (autoSlideWait && !autoSlideCancelled)
            {
                nextAutoSlide();
            }
        }).css({ transform: `translateX(-${slideDistance}px)` });
        highlightGroupControl($wrap, getGroupIndex());
    }

    function triggerChangeEvent(): void
    {
        $wrap.trigger("change", { itemIndex, numPerGroup });
    }

    function triggerPreChangeEvent(): void
    {
        $wrap.trigger("prechange", { itemIndex, numPerGroup });
    }

    function getGroupIndex(): number
    {
        const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
        const numGroups: number = ceil(numOrigItems / itemsPerGroup);
        const groupIndex: number = floor(itemIndex / itemsPerGroup);
        if (groupIndex === 0 && itemIndex > 0 && numGroups > 1)
        {
            return 1;
        }
        return groupIndex;
    }

    // Returns $items JQuery based on current value of itemIndex
    function getItemSlice(startIndex: number, sliceLength: number)
    {
        const endIndex: number = startIndex + sliceLength;
        let $items = $origItems.slice(startIndex, endIndex).clone(true, true);
        const minLength: number = max([numPerGroup, sliceLength]);
        let numMissing: number = minLength - $items.length;
        while (numMissing > 0)
        {
            $items = $items.add($origItems.slice(0, numMissing).clone(true, true));
            numMissing = minLength - $items.length;
        }
        return $items;
    }

    // returns px value
    function getVisibleItemTotalWidth(): number
    {
        return outer.clientWidth;
    }

    // returns px value
    function getTargetItemWidth(): number
    {
        return getVisibleItemTotalWidth() / numPerGroup;
    }

    // returns px value
    function getTargetItemHeight(): number
    {
        const width: number = getTargetItemWidth();
        const $items = $origItems.clone(true, true).css({
            width,
            opacity: 0,
            zIndex: -999,
            left: -width
        }).appendTo($currScroller);
        const height: number = findMaxHeight($items);
        $items.remove();
        return height;
    }

    function moveToItemIndex(newItemIndex: number): void
    {
        newItemIndex = newItemIndex % numOrigItems;
        if (newItemIndex === itemIndex)
        {
            return;
        }
        const numMoveBy: number = newItemIndex - itemIndex;
        if (numMoveBy > 0)
        {
            moveNextBy(numMoveBy);
        }
        else
        {
            movePrevBy(-numMoveBy);
        }
    }

    function moveToGroupIndex(newGroupIndex: number): void
    {
        const newItemIndex: number = (newGroupIndex * numPerGroup) % numOrigItems;
        if (newItemIndex === itemIndex)
        {
            return;
        }
        const numMoveBy: number = newItemIndex - itemIndex;
        if (numMoveBy > 0)
        {
            moveNextBy(numMoveBy);
        }
        else
        {
            movePrevBy(-numMoveBy);
        }
    }

    function prevGroup(): void
    {
        movePrevBy(numPerGroup);
    }

    function nextGroup(): void
    {
        moveNextBy(numPerGroup);
    }

    function prevItem(): void
    {
        movePrevBy(1);
    }

    function nextItem(): void
    {
        moveNextBy(1);
    }

    function nextAutoSlide(): void
	{
		clearTimeout(autoslideTimer);
		if (!autoSlideCancelled)
		{
			autoslideTimer = setTimeout(nextGroup, autoSlideWait);
		}
    }
    
    // Chevron-type controls
    $wrap.find(`[data-slider-ctrl-prev]`).on("click", chevronsByItem ? prevItem : prevGroup);
    $wrap.find(`[data-slider-ctrl-next]`).on("click", chevronsByItem ? nextItem : nextGroup);

    // Dot/number controls
    initNavControls($wrap, $window, moveToGroupIndex, itemsPerGroupSettings, numOrigItems);

    // Initialize layout & behaviour
    init();

    const initThrottled = throttle(init, 80, { trailing: true });

    // Reinitialize when layout changes
    $window.on("resize load orientationchange", initThrottled);
    $wrap.find("img").on("load", initThrottled);

    // Listen for events from external custom controls
    $wrap.on("slide-to-item", function (event, data) {
        if (!data || data.itemIndex === undefined || data.itemIndex < 0)
		{
			console.error("Bad/missing itemIndex passed to slider slide-to-item handler");
			return;
        }
        moveToItemIndex(data.itemIndex);
    }).on("slide-to-group", function (event, data) {
        if (!data || data.groupIndex === undefined || data.groupIndex < 0)
		{
			console.error("Bad/missing groupIndex passed to slider slide-to-group handler");
			return;
        }
        moveToGroupIndex(data.groupIndex);
    }).on("slide-to-prev-item", function () {
        prevItem();
    }).on("slide-to-next-item", function () {
        nextItem();
    }).on("slide-to-prev-group", function () {
        movePrevBy(numPerGroup);
    }).on("slide-to-next-group", function () {
        moveNextBy(numPerGroup);
    });
    
    // Reveal
    $wrap.addClass("show");

    // Init autoslide
    if (!autoSlideCancelled)
    {
        nextAutoSlide();
    }
}

function makeEnoughOriginalItems($wrap, itemsPerGroupSettings: ItemsPerGroupSetting[]): void
{
    const $container = $wrap.find(".slider-items-slider");
    const $origItems = $container.children();
    const minNumItems: number = findHighestItemsPerGroupValue(itemsPerGroupSettings);
    if (minNumItems === undefined)
    {
        throw new Error(`minNumItems undefined`);
    }
    while ($container.children().length < minNumItems)
    {
        $container.append($origItems.clone(true, true));
    }
}

function initNavControls(
    $wrap,
    $window,
    goToGroup: (groupIndex: number) => void,
    itemsPerGroupSettings: ItemsPerGroupSetting[],
    numItems: number): void
{
	initNavControlsForType("dot", $wrap, $window, goToGroup, itemsPerGroupSettings, numItems);
	initNavControlsForType("number", $wrap, $window, goToGroup, itemsPerGroupSettings, numItems);
}

function initNavControlsForType(
    type: "dot"|"number", 
    $wrap, 
    $window, 
    goToGroup: (groupIndex: number) => void, 
    itemsPerGroupSettings: ItemsPerGroupSetting[],
    numItems: number): void
{
	const $outerWrap = $wrap.find(`.${type}-controls`);
	const $ctrlWrap = $outerWrap.children();
	if ($ctrlWrap.length === 0)
	{
		return $outerWrap;
    }
    const isDot: boolean = (type === "dot");
	const shouldZeroPadNumber: boolean = (type === "number") && $wrap.hasClass("zero-pad-numbers");
	const reset = function reset(): void {
		const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
		const numGroups: number = ceil(numItems / itemsPerGroup);
		if (numGroups < 2)
		{
			$ctrlWrap.css({ display: "none" });
			return;
		}
		$ctrlWrap.empty().css({ display: "" });
		each(range(0, numGroups), (groupIndex: number) => {
			const numStr: string = shouldZeroPadNumber ? zeroPadNumber(groupIndex + 1) : (groupIndex + 1).toString();
			const html: string = isDot ? `<div class="dot">` : `<div class="number">${numStr}</div>`;
			$(html).appendTo($ctrlWrap).on("click", function () {
				goToGroup(groupIndex);
				return false;
			});
		});
		$outerWrap.trigger("nav-ctrl-reset");
	};
	reset();
	$window.on("resize orientationchange load", throttle(reset, 50, { trailing: true }));
	return $outerWrap;
}
